import {
  TaskCountsSchema,
  TaskPageSchema,
  TaskSchema,
  type CreateTaskDto,
  type MoveTaskDto,
  type Task,
  type TaskCounts,
  type TaskCreatedByFilter,
  type TaskPage,
  type TaskStatus,
  type TaskView,
  type UpdateTaskDto,
} from "@lumika/shared";
import { apiFetch } from "./client";

/**
 * Tasks API client. The backend (Plan 0002) exposes a queue-style
 * task list plus per-task lifecycle actions.
 */
export const tasksApi = {
  list: (
    opts: {
      view?: TaskView;
      status?: TaskStatus;
      contactId?: string;
      leadId?: string;
      rooftopId?: string;
      assigneeUserId?: string;
      createdBy?: TaskCreatedByFilter;
      search?: string;
      dueFrom?: string;
      dueTo?: string;
      includeAi?: boolean;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<TaskPage> => {
    const params = new URLSearchParams();
    if (opts.view) params.set("view", opts.view);
    if (opts.status) params.set("status", opts.status);
    if (opts.contactId) params.set("contactId", opts.contactId);
    if (opts.leadId) params.set("leadId", opts.leadId);
    if (opts.rooftopId) params.set("rooftopId", opts.rooftopId);
    if (opts.assigneeUserId) params.set("assigneeUserId", opts.assigneeUserId);
    if (opts.createdBy) params.set("createdBy", opts.createdBy);
    if (opts.search?.trim()) params.set("search", opts.search.trim());
    if (opts.dueFrom) params.set("dueFrom", opts.dueFrom);
    if (opts.dueTo) params.set("dueTo", opts.dueTo);
    if (opts.includeAi) params.set("includeAi", "true");
    if (opts.page) params.set("page", String(opts.page));
    if (opts.pageSize) params.set("pageSize", String(opts.pageSize));
    return apiFetch(`/tasks?${params.toString()}`, TaskPageSchema);
  },

  counts: (createdBy: TaskCreatedByFilter = "human"): Promise<TaskCounts> => {
    const params = new URLSearchParams();
    params.set("createdBy", createdBy);
    return apiFetch(`/tasks/counts?${params.toString()}`, TaskCountsSchema);
  },

  get: (taskId: string): Promise<Task> =>
    apiFetch(`/tasks/${taskId}`, TaskSchema),

  create: (input: Omit<CreateTaskDto, "dealershipId">): Promise<Task> =>
    apiFetch("/tasks", TaskSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Edit a task's fields — title, description, type, reason, dates. */
  update: (taskId: string, input: UpdateTaskDto): Promise<Task> =>
    apiFetch(`/tasks/${taskId}`, TaskSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  /** Snooze a task — moves it to `snoozed` until `availableAt`. */
  snooze: (taskId: string, availableAt: string): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/snooze`, TaskSchema, {
      method: "POST",
      body: JSON.stringify({ availableAt }),
    }),

  complete: (taskId: string): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/complete`, TaskSchema, { method: "POST" }),

  cancel: (taskId: string): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/cancel`, TaskSchema, { method: "POST" }),

  start: (taskId: string): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/start`, TaskSchema, { method: "POST" }),

  /** Move a task straight to a status — the Kanban drag-and-drop. */
  move: (taskId: string, status: MoveTaskDto["status"]): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/move`, TaskSchema, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  assign: (taskId: string, assignedUserId: string | null): Promise<Task> =>
    apiFetch(`/tasks/${taskId}/assign`, TaskSchema, {
      method: "POST",
      body: JSON.stringify({ assignedUserId }),
    }),
};
